/**
 * Sentencing Research workspace — standalone Arc chat over the Montana
 * sentencing dataset. No case required.
 *
 * Mirrors the help drawer's chat flow (HelpChatPanel) against the
 * /sentencing endpoints: a conversation is created lazily on first send,
 * each turn POSTs the message and renders the answer with ChatMessage,
 * which dispatches the sentencing tool cards (records table, facets).
 *
 * Access: the route only renders useful UI when /auth/me reports
 * features.sentencing_tools (NavBar hides the tab otherwise); the backend
 * 403s unentitled calls regardless.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import ChatMessage from '../components/ChatMessage';
import SentencingBrowse from '../components/SentencingBrowse';
import { useUserProfile } from '../hooks/useUserProfile';
import { parseBrowseParams } from '../domain/sentencing';
import './SentencingResearch.css';

const SUGGESTIONS = [
  'What sentences have been imposed for deliberate homicide in Yellowstone County?',
  'Which judges have sentenced the most DUI cases?',
  'What do people under 25 get for burglary with a weapon?',
  'What is the typical term for felony theft in Missoula County?',
];
// Offered only where the opinions are loaded (metadata.opinions).
const OPINION_SUGGESTIONS = [
  'Find sentences for negligent homicide where a vehicle was involved.',
  'What has the Montana Supreme Court held about restitution and ability to pay?',
];

function longDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
    // A bare date (an opinion's filing date) is that calendar day everywhere,
    // not midnight UTC shown in local time.
    ...(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? { timeZone: 'UTC' } : {}),
  });
}

function relativeDay(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

export default function SentencingResearch() {
  const { profile, loading: profileLoading } = useUserProfile();
  const enabled = Boolean(profile?.features?.sentencing_tools);

  // 'ask' | 'browse'. A chat card's "Open in Browse" link arrives as ?mode=browse&f=…
  const [mode, setMode] = useState(
    () => (parseBrowseParams(window.location.search).mode === 'browse' ? 'browse' : 'ask'),
  );
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingConv, setLoadingConv] = useState(false);
  const [datasetMeta, setDatasetMeta] = useState(null);

  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const refreshHistory = useCallback(async () => {
    try {
      setHistory(await api.get('/sentencing/conversations'));
    } catch {
      // History is a convenience rail — a failed load shouldn't block chat.
    }
  }, []);

  useEffect(() => {
    if (enabled) refreshHistory();
  }, [enabled, refreshHistory]);

  useEffect(() => {
    if (!enabled) return;
    api.get('/sentencing/metadata')
      .then(setDatasetMeta)
      .catch(() => {
        // Freshness is informational — the banner just omits the date.
      });
  }, [enabled]);

  const openConversation = useCallback(async (id) => {
    if (busy || loadingConv || id === conversationId) return;
    setLoadingConv(true);
    setError(null);
    try {
      const conv = await api.get(`/sentencing/conversations/${id}`);
      setConversationId(conv.id);
      setMessages((conv.messages || []).map((m) => ({
        id: m.id,
        role: m.role,
        content: m.content,
        citations: m.citations || [],
        tool_trace: m.tool_trace || [],
      })));
    } catch (e) {
      setError(e.message || 'Failed to load conversation');
    } finally {
      setLoadingConv(false);
    }
  }, [busy, loadingConv, conversationId]);

  const newChat = useCallback(() => {
    if (busy) return;
    setConversationId(null);
    setMessages([]);
    setError(null);
    setInput('');
  }, [busy]);

  const ensureConversation = useCallback(async () => {
    if (conversationId) return conversationId;
    const created = await api.post('/sentencing/conversations', {});
    setConversationId(created.id);
    return created.id;
  }, [conversationId]);

  const send = useCallback(async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || busy) return;
    setError(null);
    setBusy(true);
    setInput('');

    const optimisticUser = { role: 'user', content: text, optimistic: true };
    setMessages((prev) => [...prev, optimisticUser]);

    try {
      const cid = await ensureConversation();
      const res = await api.post(`/sentencing/conversations/${cid}/messages`, {
        message: text,
      });

      const assistantMsg = {
        id: `asst-${Date.now()}`,
        role: 'assistant',
        content: res.answer || '',
        citations: res.citations || [],
        tool_trace: res.tool_trace || [],
        tokens_in: res.tokens_in,
        tokens_out: res.tokens_out,
        duration_ms: res.duration_ms,
        confidence: res.confidence,
      };
      setMessages((prev) => [
        ...prev.filter((m) => !m.optimistic),
        { id: `user-${Date.now()}`, role: 'user', content: text },
        assistantMsg,
      ]);
      refreshHistory(); // first turn names the conversation server-side
    } catch (e) {
      setError(e.message || 'Something went wrong. Try again.');
      setMessages((prev) => prev.filter((m) => !m.optimistic));
    } finally {
      setBusy(false);
    }
  }, [input, busy, ensureConversation, refreshHistory]);

  const onComposerKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!profileLoading && !enabled) {
    return (
      <div className="sentencing-page">
        <div className="sentencing-empty">
          <h2>Sentencing Research</h2>
          <p>This workspace isn't enabled for your account.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`sentencing-page${mode === 'browse' ? ' sentencing-page--browse' : ''}`}>
      <header className="sentencing-header">
        <div>
          <h2>Sentencing Research</h2>
          <p className="sentencing-subtitle">
            Find Montana sentences by charge, place, judge, person and
            circumstances. Historical public-record data, not legal advice.
          </p>
        </div>
        <div className="sentencing-mode-toggle" role="tablist">
          <button
            role="tab"
            aria-selected={mode === 'ask'}
            className={`sentencing-mode-btn${mode === 'ask' ? ' active' : ''}`}
            onClick={() => setMode('ask')}
          >
            Ask
          </button>
          <button
            role="tab"
            aria-selected={mode === 'browse'}
            className={`sentencing-mode-btn${mode === 'browse' ? ' active' : ''}`}
            onClick={() => setMode('browse')}
          >
            Browse
          </button>
        </div>
      </header>

      <div className="sentencing-disclaimer" role="note">
        <span>
          This data comes from the Montana Department of Corrections
          {datasetMeta?.opinions ? ' and from Montana Supreme Court opinions and Sentence Review decisions' : ''}.
          Chargestack makes no guarantees about its accuracy or completeness —
          always check the original judgment and sentence
          {datasetMeta?.opinions ? ' and read the opinion' : ''} before relying on it.
        </span>
        {longDate(datasetMeta?.source_last_modified || datasetMeta?.last_updated) && (
          <span className="sentencing-disclaimer-updated">
            {datasetMeta?.doc_exports?.length
              ? `DOC data as of ${longDate(datasetMeta.doc_exports[datasetMeta.doc_exports.length - 1].export_date)}`
              : `Data last updated ${longDate(datasetMeta?.source_last_modified || datasetMeta?.last_updated)}`}
            {longDate(datasetMeta?.opinions?.latest_filed) && ` · opinions through ${longDate(datasetMeta.opinions.latest_filed)}`}
          </span>
        )}
      </div>

      {mode === 'browse' && (
        <div className="sentencing-chat">
          <SentencingBrowse opinionsLoaded={Boolean(datasetMeta?.opinions)} />
        </div>
      )}

      <div className="sentencing-ask-layout" style={{ display: mode === 'ask' ? 'flex' : 'none' }}>
        <aside className="sentencing-history">
          <button className="sentencing-newchat" onClick={newChat} disabled={busy}>
            + New chat
          </button>
          <div className="sentencing-history-list">
            {history.length === 0 && (
              <div className="sentencing-history-empty">No past conversations</div>
            )}
            {history.map((h) => (
              <button
                key={h.id}
                className={`sentencing-history-item${h.id === conversationId ? ' active' : ''}`}
                onClick={() => openConversation(h.id)}
                title={h.title || 'Untitled'}
              >
                <span className="sentencing-history-title">{h.title || 'Untitled'}</span>
                <span className="sentencing-history-date">{relativeDay(h.updated_at)}</span>
              </button>
            ))}
          </div>
        </aside>

        <div className="sentencing-chat">
        <div className="sentencing-messages" ref={scrollRef}>
          {loadingConv && <div className="sentencing-thinking">Loading conversation…</div>}
          {messages.length === 0 && !busy && !loadingConv && (
            <div className="sentencing-suggestions">
              <div className="sentencing-suggestions-label">Try asking:</div>
              {[...SUGGESTIONS, ...(datasetMeta?.opinions ? OPINION_SUGGESTIONS : [])].map((s) => (
                <button
                  key={s}
                  className="sentencing-suggestion"
                  onClick={() => send(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          {messages.map((m, i) => (
            <ChatMessage key={m.id || `msg-${i}`} msg={m} citations={m.citations} />
          ))}
          {busy && <div className="sentencing-thinking">Researching…</div>}
          {error && <div className="sentencing-error">{error}</div>}
        </div>

        <div className="sentencing-composer">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onComposerKeyDown}
            placeholder="Ask about Montana sentencing data…"
            disabled={busy}
            rows={2}
          />
          <button
            className="btn btn-primary"
            onClick={() => send()}
            disabled={busy || !input.trim()}
          >
            Send
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}
